import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { fmt, PieChart, CompoundSelect, effectiveAnnual } from './_finHelpers';

// Savings Calculator — mirrors image with contribution increases
// FIX (Feb 2026): the "Compound" dropdown was previously not affecting the math.
// Now the annual rate is converted to a monthly rate using the selected compounding
// convention: e.g. "Monthly (APR)" treats `rate` as APR (÷12), "Annually (APY)" treats
// it as APY ((1+r)^(1/12) − 1), etc.
export default function SavingsCalc() {
  const [initial, setInitial] = useState(20000);
  const [annualContrib, setAnnualContrib] = useState(5000);
  const [annualInc, setAnnualInc] = useState(3);
  const [monthlyContrib, setMonthlyContrib] = useState(0);
  const [monthlyInc, setMonthlyInc] = useState(0);
  const [rate, setRate] = useState(3);
  const [compound, setCompound] = useState('annually');
  const [years, setYears] = useState(10);
  const [tax, setTax] = useState(0);
  const c = useMemo(() => {
    let bal = initial;
    let curAnn = annualContrib;
    let curMon = monthlyContrib;
    // Convert nominal rate + compounding convention → effective monthly rate
    const effAnnual = effectiveAnnual(rate, compound);
    const rMon = Math.pow(1 + effAnnual, 1/12) - 1;
    const rows = [];
    let totalContrib = initial;                // initial counted in "deposit" like Calculator.net
    let totalInt = 0;
    for (let y = 1; y <= years; y++) {
      let yInt = 0;
      let yContrib = 0;
      if (y === 1) yContrib += initial;        // include starting deposit in year 1 row
      // Monthly steps: interest first, then monthly contribution at end (ordinary annuity)
      for (let m = 0; m < 12; m++) {
        const int = bal * rMon * (1 - tax/100);
        bal += int;
        yInt += int;
        bal += curMon;
        yContrib += curMon;
      }
      // Annual contribution at end of year
      bal += curAnn;
      yContrib += curAnn;
      totalContrib += curAnn + curMon * 12;
      totalInt += yInt;
      rows.push({ year: y, deposit: yContrib, interest: yInt, balance: bal });
      curAnn *= (1 + annualInc/100);
      curMon *= (1 + monthlyInc/100);
    }
    return { end: bal, totalContrib: totalContrib - initial, totalInt, rows, effAnnual };
  }, [initial, annualContrib, annualInc, monthlyContrib, monthlyInc, rate, compound, years, tax]);
  return (
    <CalcShell look="cnet" title="Savings Calculator" subtitle="Project your savings account balance with recurring deposits, escalating contributions, and taxes."
      about={<><p>Model long-term savings with variable deposits. The calculator supports both annual and monthly contributions and allows each to escalate at its own annual rate.</p></>}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Initial deposit</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={initial} onChange={e=>setInitial(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>Annual contribution</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={annualContrib} onChange={e=>setAnnualContrib(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>Annual contribution increase</label><div className="flex gap-2"><input type="number" step="0.01" value={annualInc} onChange={e=>setAnnualInc(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">% / year</span></div></div>
          <div className="field"><label>Monthly contribution</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={monthlyContrib} onChange={e=>setMonthlyContrib(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>Monthly contribution increase</label><div className="flex gap-2"><input type="number" step="0.01" value={monthlyInc} onChange={e=>setMonthlyInc(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">% / year</span></div></div>
          <div className="field"><label>Interest rate</label><div className="flex gap-2"><input type="number" step="0.01" value={rate} onChange={e=>setRate(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
          <div className="field"><label>Compound</label><CompoundSelect value={compound} onChange={setCompound}/></div>
          <div className="field"><label>Years to save</label><div className="flex gap-2"><input type="number" value={years} onChange={e=>setYears(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">years</span></div></div>
          <div className="field"><label>Tax rate</label><div className="flex gap-2"><input type="number" step="0.01" value={tax} onChange={e=>setTax(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
        </div>
        <div>
          <div className="result-panel mb-4"><div className="result-title">End balance</div><div className="result-value" data-testid="sav-end">{fmt(c.end)}</div></div>
          <table className="data-table"><tbody>
            <tr><td>Initial deposit</td><td className="text-right">{fmt(initial)}</td></tr>
            <tr><td>Total contributions</td><td className="text-right">{fmt(c.totalContrib)}</td></tr>
            <tr><td>Total interest earned</td><td className="text-right text-orange-600">{fmt(c.totalInt)}</td></tr>
          </tbody></table>
          <div className="mt-4"><PieChart parts={[{label:'Initial deposit',value:initial,color:'#2563eb'},{label:'Contributions',value:c.totalContrib,color:'#10b981'},{label:'Interest',value:c.totalInt,color:'#dc2626'}]} size={180}/></div>
        </div>
      </div>
      <div className="calc-card p-5 mt-6">
        <h3 className="font-bold text-slate-900 mb-3">Accumulation Schedule</h3>
        <div className="max-h-96 overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-blue-800 text-white sticky top-0"><tr><th className="px-3 py-2 text-left">Year</th><th className="px-3 py-2 text-right">Deposit</th><th className="px-3 py-2 text-right">Interest</th><th className="px-3 py-2 text-right">Ending balance</th></tr></thead>
            <tbody>
              {c.rows.map((r,i)=>(<tr key={i} className={`border-b ${i%2?'bg-slate-50':''}`}><td className="px-3 py-2">{r.year}</td><td className="px-3 py-2 text-right">{fmt(r.deposit)}</td><td className="px-3 py-2 text-right text-orange-600">{fmt(r.interest)}</td><td className="px-3 py-2 text-right font-semibold">{fmt(r.balance)}</td></tr>))}
            </tbody>
          </table>
        </div>
      </div>
    </CalcShell>
  );
}
